import { useEffect, useRef, useState } from 'react';

const DEMO_WINDOW_DESIGN_WIDTH = 1280;
export const DEMO_WINDOW_DESIGN_HEIGHT = 720;
export const DEMO_RESPONSIVE_MIN_VIEWPORT_WIDTH = 1024;
export const DEMO_HERO_SIDEBAR_MIN_VIEWPORT_WIDTH = 1200;

export type DemoScaleToFitOptions = {
  designWidth?: number;
  designHeight?: number;
  enabled?: boolean;
};

export const useMinViewportWidth = (minWidth: number) => {
  const query = `(min-width: ${minWidth}px)`;
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const sync = () => setMatches(mql.matches);
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, [query]);

  return matches;
};

export const useDemoScaleToFit = (options?: DemoScaleToFitOptions) => {
  const designWidth = options?.designWidth ?? DEMO_WINDOW_DESIGN_WIDTH;
  const designHeight = options?.designHeight ?? DEMO_WINDOW_DESIGN_HEIGHT;
  const enabled = options?.enabled ?? true;
  const hostRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!enabled) {
      setScale(1);
      return;
    }

    const host = hostRef.current;
    if (!host) return;

    const updateScale = () => {
      const style = getComputedStyle(host);
      const paddingX =
        parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const paddingY =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const width = host.clientWidth - paddingX;
      const height = host.clientHeight - paddingY;
      if (width <= 0) return;
      const scaleW = width / designWidth;
      const scaleH = height > 0 ? height / designHeight : scaleW;
      setScale(Math.min(1, scaleW, scaleH));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(host);
    return () => observer.disconnect();
  }, [designWidth, designHeight, enabled]);

  return { hostRef, scale };
};
